import { ChangeEvent, useEffect, useState } from 'react';
import {
  DatePicker,
  Divider,
  RadioButton,
  RadioButtonGroup,
  Switcher,
  SwitcherGroup,
  SwitcherGroupDivider,
} from 'omakase';
import { LocalDate } from 'js-joda';
import {
  QuickFilterSelectionType,
  getDateRangeResolver,
  getQuickFilterType,
} from 'utils/search/quickFilterUtils';
import { css } from '@emotion/react';

interface Props {
  startDate: LocalDate;
  endDate: LocalDate;
  dateEnd?: string;
  date?: string;
  onChangeDate(date: string): void;
  onChangeDateRange?(startDate?: LocalDate, endDate?: LocalDate): void;
}

export const CalendarWithQuickFilter: React.FunctionComponent<Props> = ({
  startDate,
  endDate,
  dateEnd,
  date,
  onChangeDate,
  onChangeDateRange,
}) => {
  const [quickFilterState, setQuickFilterEnable] = useState(true);
  const [selectedOption, setQuickFilterOption] = useState<QuickFilterSelectionType | undefined>(
    undefined
  );
  //track currently selected date range within the quick filter and calendar.  This date range
  //can be different than startDate, endDate, and date and dateEnd.
  const [filterStartDate, setFilterStartDate] = useState<LocalDate | undefined>(undefined);
  const [filterEndDate, setFilterEndDate] = useState<LocalDate | undefined>(undefined);
  const dateRangeResolver = getDateRangeResolver();

  useEffect(() => {
    const filterType = getQuickFilterType(date, dateEnd ?? date);
    if (filterType !== undefined) {
      setQuickFilterOption(filterType);
    } else {
      setQuickFilterEnable(false);
    }
  }, [date, dateEnd]);

  const onSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFilterType = event.target.value as QuickFilterSelectionType;
    setQuickFilterOption(selectedFilterType);
    const { newStartDate, newEndDate } = dateRangeResolver.get(selectedFilterType)?.() ?? {
      newStartDate: LocalDate.now(),
      newEndDate: LocalDate.now(),
    };
    setFilterStartDate(newStartDate);
    setFilterEndDate(newEndDate);
    onChangeDateRange?.(newStartDate, selectedFilterType !== 'today' ? newEndDate : undefined);
  };

  const setQuickFilterType = () => {
    const tmpStartDate =
      filterStartDate ?? (date !== undefined ? LocalDate.parse(date) : startDate);
    const tmpEndDate =
      filterEndDate ?? (dateEnd !== undefined ? LocalDate.parse(dateEnd) : endDate);
    const matchingFilterType = getQuickFilterType(tmpStartDate.toString(), tmpEndDate.toString());
    setQuickFilterOption(matchingFilterType);
  };

  const onDateRangeSelected = (startDate?: LocalDate, endDate?: LocalDate) => {
    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
    if (onChangeDateRange !== undefined) {
      onChangeDateRange(startDate, endDate);
    }
  };

  return (
    <>
      <div
        css={css`
          padding: 0 4px;
        `}
      >
        <SwitcherGroup background={false}>
          <Switcher
            checked={quickFilterState}
            type="radio"
            label={'Quick filter'}
            name={'quickfilter'}
            variant={'basic'}
            onChange={() => {
              setQuickFilterEnable(true);
              setQuickFilterType();
            }}
          />
          <SwitcherGroupDivider />
          <Switcher
            checked={!quickFilterState}
            type="radio"
            label={'Calendar'}
            name={'calendar'}
            variant={'basic'}
            onChange={() => {
              setQuickFilterEnable(false);
            }}
          />
        </SwitcherGroup>
      </div>
      <Divider mt={2} />
      <div
        css={css`
          padding: 0 20px;
        `}
      >
        {quickFilterState && (
          <RadioButtonGroup disclosureMode="static" name="reason" label="">
            <RadioButton
              size="large"
              weight="book"
              value="upcoming"
              label="All upcoming"
              onChange={(event) => onSelection(event)}
              checked={selectedOption === 'upcoming'}
            />
            <RadioButton
              size="large"
              weight="book"
              value="today"
              label="Today"
              onChange={(event) => onSelection(event)}
              checked={selectedOption === 'today'}
            />
            <RadioButton
              size="large"
              weight="book"
              value="week"
              label="This week"
              onChange={(event) => onSelection(event)}
              checked={selectedOption === 'week'}
            />
            <RadioButton
              size="large"
              weight="book"
              value="weekend"
              label="This weekend"
              onChange={(event) => onSelection(event)}
              checked={selectedOption === 'weekend'}
            />
            <RadioButton
              size="large"
              weight="book"
              value="month"
              label="This month"
              onChange={(event) => onSelection(event)}
              checked={selectedOption === 'month'}
            />
            <RadioButton
              size="large"
              weight="book"
              value="nextmonth"
              label="Next month"
              onChange={(event) => onSelection(event)}
              checked={selectedOption === 'nextmonth'}
            />
          </RadioButtonGroup>
        )}
        <div
          css={css`
            margin-top: 20px;
            margin-bottom: 20px;
          `}
        >
          {!quickFilterState && (
            <DatePicker
              selectionType="range"
              selectedRange={{
                from: date !== undefined ? LocalDate.parse(date) : startDate,
                to:
                  dateEnd !== undefined
                    ? LocalDate.parse(dateEnd)
                    : date !== undefined
                    ? LocalDate.parse(date)
                    : startDate,
              }}
              onDateRangeSelected={onDateRangeSelected}
            />
          )}
        </div>
      </div>
    </>
  );
};
